import { requireFileAction } from '../../services/menu/file-action.service';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import editUserServices, { FlagHistoryItem } from '../../services/edit-users/edit-users.service';
import Helpers from '../../utils/helpers';

// exceljs is used (not SheetJS CE) because it supports wrap text, so each flag
// history entry shows on its own line inside the cell.
const saveWorkbook = async (
  rows: Record<string, any>[],
  widths: number[],
  fileName: string,
  blueRows: boolean[] = []
) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Customers');
  const headers = Object.keys(rows[0]);
  ws.columns = headers.map((h, i) => ({ header: h, key: h, width: widths[i] ?? 15 }));
  rows.forEach((r) => ws.addRow(r));
  ws.getRow(1).font = { bold: true };
  ws.eachRow((row, rowNumber) => {
    row.alignment = { vertical: 'top', wrapText: true };
    // rowNumber 1 is the header; data rows start at 2
    if (blueRows[rowNumber - 2]) {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFDBFE' } };
      });
    }
  });
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    fileName
  );
};

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAmount = (amount: number): string => {
  if (!amount || amount === 0) return '';
  try {
    return Helpers.formatAmountInINR(amount);
  } catch {
    return amount.toString();
  }
};

const flagLabel = (code: number): string =>
  code === 1 ? 'Red Flag' : code === 2 ? 'Blue Flag' : 'Neutral';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return `${formatDate(date)} ${date.toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })}`;
};

// Only the latest flag update (same ordering as the Flag History dialog)
const latestFlagEntry = (history: FlagHistoryItem[]): FlagHistoryItem | undefined =>
  [...history]
    .filter((h) => h.actionType !== 'I')
    .sort(
      (a, b) =>
        (b.updateDate ? new Date(b.updateDate).getTime() : 0) - (a.updateDate ? new Date(a.updateDate).getTime() : 0) ||
        (b.auditID ?? 0) - (a.auditID ?? 0) // a flag update writes 2 rows with the same timestamp; newest ID wins
    )[0];

const buildFlagHistoryText = (h: FlagHistoryItem | undefined): string => {
  if (!h) return '';
  const meta = [h.updatedByName, formatDateTime(h.updateDate)].filter(Boolean).join(', ');
  return `${flagLabel(h.flagCode)}${meta ? ` (${meta})` : ''}${h.comments ? `: ${h.comments}` : ''}`;
};

const fetchFlagHistories = async (customers: any[]): Promise<Record<number, FlagHistoryItem | undefined>> => {
  const result: Record<number, FlagHistoryItem | undefined> = {};
  const ids = Array.from(
    new Set(customers.map((c) => c.userID ?? c.userId).filter((id) => id !== undefined && id !== null))
  ) as number[];
  const batchSize = 10;
  for (let i = 0; i < ids.length; i += batchSize) {
    await Promise.all(
      ids.slice(i, i + batchSize).map(async (id) => {
        result[id] = latestFlagEntry(await editUserServices.getFlagHistory(id));
      })
    );
  }
  return result;
};

const isBlueRow = (customers: any[], flags: Record<number, FlagHistoryItem | undefined>): boolean[] =>
  customers.map((c) => flags[c.userID ?? c.userId]?.flagCode === 2);

const customerWiseData = (customers: any[], flags: Record<number, FlagHistoryItem | undefined> = {}) => {
  return customers.map((c) => ({
    'User Name': c.name ?? '',
    'Primary Contact': c.primaryContactNbr ?? '',
    'Secondary Contact': c.secondaryContactNbr ?? '',
    'Email ID': c.email ?? '',
    'GST No.': c.gsT_Number ?? c.GST_Number ?? '',
    'Referred By': c.referredByName ?? '',
    'Created Date': formatDate(c.createdDate),
    'Invoice Amount': formatAmount(c.invoiceAmount ?? 0),
    'Last Order ID': c.orderId === 0 ? '' : (c.orderId ?? ''),
    'Invoice Date': formatDate(c.invoiceDate),
    'Last Order Date': formatDate(c.orderDate),
    'Status': c.isDeleted ? 'Deleted' : 'Active',
    'Flag History': buildFlagHistoryText(flags[c.userID ?? c.userId]),
  }));
};

export const exportCustomers = async (filter: { startDate?: string; endDate?: string }) => {
  await requireFileAction('export', window.location.pathname, 12);
  try {
    const data = await editUserServices.exportAllCustomers(filter);

    if (!data || data.length === 0) {
      console.warn('No customer data available for export.');
      return;
    }

    const flags = await fetchFlagHistories(data);
    const exportData = customerWiseData(data, flags);
    const columnWidths = [22, 16, 16, 28, 18, 22, 15, 15, 14, 14, 14, 10, 60];

    // Build file name with date range if provided
    let fileName = 'CustomerListExport';
    if (filter.startDate || filter.endDate) {
      const from = filter.startDate ? `_from_${filter.startDate}` : '';
      const to = filter.endDate ? `_to_${filter.endDate}` : '';
      fileName += `${from}${to}`;
    }
    await saveWorkbook(exportData, columnWidths, `${fileName}.xlsx`, isBlueRow(data, flags));

    console.log(`✅ Exported ${exportData.length} customers successfully.`);
  } catch (error) {
    console.error('❌ Error exporting customers:', error);
  }
};

/**
 * Exports the data that is currently displayed in the datatable.
 * This respects all active filters (keyword, user, date range).
 */
export const exportCustomersFromData = async (data: any[]) => {
  await requireFileAction('export', window.location.pathname, 12);
  try {
    if (!data || data.length === 0) {
      console.warn('No customer data to export.');
      return;
    }

    const flags = await fetchFlagHistories(data);
    const exportData = customerWiseData(data, flags);
    const columnWidths = [22, 16, 16, 28, 18, 22, 15, 15, 14, 14, 14, 10, 60];
    await saveWorkbook(exportData, columnWidths, `CustomerListExport_${new Date().toISOString().slice(0, 10)}.xlsx`, isBlueRow(data, flags));
    console.log(`✅ Exported ${exportData.length} customers successfully.`);
  } catch (error) {
    console.error('❌ Error exporting customers:', error);
  }
};
